import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import {
  getDailyRollupByEditions,
  getEditionsForAnalytics,
  getEditionTotals,
  getHotColdSubscribers,
  getSubscriberGrowth,
  EditionOption,
  DailyRollupEntry,
  EditionTotals as EditionTotalsType,
  HotColdSubscribers as HotColdType,
  SubscriberGrowthEntry,
} from "../services/analyticsService";
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format } from "date-fns";
import { useStatsigClient } from "@statsig/react-bindings";
import EditionMultiSelect from "./EditionMultiSelect";

const MIN_START_DATE = new Date("2025-05-26T00:00:00");
const DAYS_TO_MS = 24 * 60 * 60 * 1000;
const SEVEN_DAYS_AGO = new Date(Date.now() - 7 * DAYS_TO_MS);
const YESTERDAY = new Date(Date.now() - 1 * DAYS_TO_MS);
const TODAY = new Date(Date.now());

const DATE_PRESETS: { label: string; getRange: () => [Date, Date] }[] = [
  {
    label: "This Week",
    getRange: () => {
      const now = new Date();
      const day = now.getDay();
      const monday = new Date(now);
      monday.setDate(now.getDate() - ((day + 6) % 7));
      monday.setHours(0, 0, 0, 0);
      return [monday, TODAY];
    },
  },
  {
    label: "This Month",
    getRange: () => {
      const now = new Date();
      return [new Date(now.getFullYear(), now.getMonth(), 1), TODAY];
    },
  },
  {
    label: "This Quarter",
    getRange: () => {
      const now = new Date();
      const qStart = Math.floor(now.getMonth() / 3) * 3;
      return [new Date(now.getFullYear(), qStart, 1), TODAY];
    },
  },
  {
    label: "Last 30 days",
    getRange: () => [new Date(Date.now() - 30 * DAYS_TO_MS), YESTERDAY],
  },
  {
    label: "Last 90 days",
    getRange: () => [new Date(Date.now() - 90 * DAYS_TO_MS), YESTERDAY],
  },
];

const MAX_EDITION_LABEL_LENGTH = 20;

const COLORS = {
  delivered: "#9F7AEA",
  opened: "#38B2AC",
  unique_opened: "#2B6CB0",
  clicked: "#F687B3",
  unique_clicked: "#D53F8C",
  bounced: "#E53E3E",
  unsubscribed: "#718096",
  subscribers: "#4299E1",
  hot: "#38B2AC",
  cold: "#CBD5E0",
} as const;

const PIE_COLORS = [COLORS.hot, COLORS.cold];

type StepOption = "daily" | "weekly" | "monthly" | "quarterly";

const STEP_OPTIONS: { value: StepOption; label: string }[] = [
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
];

const VALID_STEPS = new Set<string>(["daily", "weekly", "monthly", "quarterly"]);

const isValidDateString = (dateString: string | null): boolean => {
  if (!dateString) return false;
  const isoDateRegex = /^\d{4}-\d{2}-\d{2}$/;
  if (!isoDateRegex.test(dateString)) return false;
  const date = new Date(`${dateString}T00:00:00`);
  return !isNaN(date.getTime());
};

const formatDay = (day: string) =>
  new Date(`${day}T00:00:00`).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });

const formatLabel = (day: string, step: StepOption): string => {
  if (step === "daily") return formatDay(day);
  if (step === "weekly") {
    const d = new Date(`${day}T00:00:00`);
    return `Wk of ${d.toLocaleDateString("en-US", { month: "short", day: "numeric" })}`;
  }
  if (step === "monthly") {
    const d = new Date(`${day}T00:00:00`);
    return d.toLocaleDateString("en-US", { month: "short", year: "numeric" });
  }
  // quarterly
  const d = new Date(`${day}T00:00:00`);
  const q = Math.ceil((d.getMonth() + 1) / 3);
  return `Q${q} ${d.getFullYear()}`;
};

// Skeleton components
const SkeletonRect: React.FC<{ width?: string; height: number }> = ({
  width = "100%",
  height,
}) => (
  <div
    style={{
      width,
      height,
      background: "linear-gradient(90deg, #e2e8f0 25%, #edf2f7 50%, #e2e8f0 75%)",
      backgroundSize: "200% 100%",
      animation: "shimmer 1.5s infinite",
      borderRadius: "8px",
    }}
  />
);

const SkeletonCircle: React.FC<{ size: number }> = ({ size }) => (
  <div
    style={{
      width: size,
      height: size,
      borderRadius: "50%",
      background: "linear-gradient(90deg, #e2e8f0 25%, #edf2f7 50%, #e2e8f0 75%)",
      backgroundSize: "200% 100%",
      animation: "shimmer 1.5s infinite",
      margin: "0 auto",
    }}
  />
);

const SkeletonStatCard: React.FC = () => (
  <div
    style={{
      flex: 1,
      padding: "16px",
      borderRadius: "8px",
      background: "#f7fafc",
      textAlign: "center",
    }}
  >
    <SkeletonRect height={14} width="60%" />
    <div style={{ marginTop: "8px" }}>
      <SkeletonRect height={28} width="40%" />
    </div>
  </div>
);

// Custom tooltip for bar chart with percentages
const EditionBarTooltip: React.FC<any> = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const delivered = payload.find((p: any) => p.dataKey === "delivered")?.value || 0;
  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #e2e8f0",
        borderRadius: "8px",
        padding: "12px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
      }}
    >
      <p style={{ margin: "0 0 8px", fontWeight: "bold" }}>{label}</p>
      {payload.map((entry: any) => {
        const pct = delivered > 0 ? ((entry.value / delivered) * 100).toFixed(1) : "0.0";
        const showPct = entry.dataKey !== "delivered";
        return (
          <p key={entry.dataKey} style={{ margin: "2px 0", color: entry.color }}>
            {entry.name}: {entry.value.toLocaleString()}
            {showPct && ` (${pct}%)`}
          </p>
        );
      })}
    </div>
  );
};

const Analytics: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();

  const startDateParam = searchParams.get("start_date");
  const endDateParam = searchParams.get("end_date");
  const editionsParam = searchParams.get("editions");
  const stepParam = searchParams.get("step");
  const initialStep: StepOption = stepParam && VALID_STEPS.has(stepParam)
    ? (stepParam as StepOption)
    : "daily";

  const initialStartDate = isValidDateString(startDateParam)
    ? new Date(`${startDateParam}T00:00:00`)
    : SEVEN_DAYS_AGO;
  const initialEndDate = isValidDateString(endDateParam)
    ? new Date(`${endDateParam}T00:00:00`)
    : YESTERDAY;

  // Data states
  const [dailyRollup, setDailyRollup] = useState<DailyRollupEntry[]>([]);
  const [editionTotals, setEditionTotals] = useState<EditionTotalsType[]>([]);
  const [hotCold, setHotCold] = useState<HotColdType | null>(null);
  const [subscriberGrowth, setSubscriberGrowth] = useState<SubscriberGrowthEntry[]>([]);
  const [editions, setEditions] = useState<EditionOption[]>([]);

  // Loading states (independent per section)
  const [rollupLoading, setRollupLoading] = useState(true);
  const [editionTotalsLoading, setEditionTotalsLoading] = useState(true);
  const [hotColdLoading, setHotColdLoading] = useState(true);
  const [growthLoading, setGrowthLoading] = useState(true);
  const [editionsLoading, setEditionsLoading] = useState(true);

  // Filter states
  const [selectedEditionsIds, setSelectedEditionsIds] = useState<number[]>([]);
  const [selectedStep, setSelectedStep] = useState<StepOption>(initialStep);
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([
    initialStartDate,
    initialEndDate,
  ]);

  // Legend toggle states
  const [hiddenAreaSeries, setHiddenAreaSeries] = useState<Set<string>>(new Set(["clicked", "unique_clicked", "opened"]));
  const [hiddenBarSeries, setHiddenBarSeries] = useState<Set<string>>(new Set());
  const [hideEmptyEditions, setHideEmptyEditions] = useState(false);

  // Subscriber growth tab: "total" (area chart) or "daily_change" (bar chart)
  const [growthTab, setGrowthTab] = useState<"total" | "daily_change">("total");

  const [startDate, endDate] = dateRange;

  const { checkGate } = useStatsigClient();
  const shouldHideAnalytics = checkGate("should_hide_analytics");

  // Load editions on mount
  useEffect(() => {
    getEditionsForAnalytics()
      .then((data) => {
        setEditions(data);
        if (editionsParam) {
          const editionIdsFromUrl = editionsParam
            .split(",")
            .filter(Boolean)
            .map((id) => parseInt(id, 10))
            .filter((id) => !isNaN(id));
          const validEditionIds = editionIdsFromUrl.filter((id) =>
            data.some((e) => e.id === id)
          );
          setSelectedEditionsIds(validEditionIds);
        }
      })
      .catch(console.error)
      .finally(() => setEditionsLoading(false));
  }, []);

  const editionsToFetch = useMemo(() => {
    if (
      selectedEditionsIds.length === 0 ||
      selectedEditionsIds.length === editions.length
    ) {
      return [];
    }
    return selectedEditionsIds;
  }, [selectedEditionsIds, editions.length]);

  // Fetch daily rollup + edition totals + subscriber growth (date-dependent)
  useEffect(() => {
    if (!startDate || !endDate || shouldHideAnalytics || editionsLoading) return;

    setRollupLoading(true);
    getDailyRollupByEditions(startDate, endDate, editionsToFetch, selectedStep)
      .then(setDailyRollup)
      .catch(console.error)
      .finally(() => setRollupLoading(false));

    setEditionTotalsLoading(true);
    getEditionTotals(startDate, endDate, editionsToFetch)
      .then(setEditionTotals)
      .catch(console.error)
      .finally(() => setEditionTotalsLoading(false));

    setGrowthLoading(true);
    getSubscriberGrowth(startDate, endDate, selectedStep)
      .then(setSubscriberGrowth)
      .catch(console.error)
      .finally(() => setGrowthLoading(false));
  }, [startDate, endDate, editionsToFetch, selectedStep, shouldHideAnalytics, editionsLoading]);

  // Fetch hot/cold (edition-dependent, no date dependency)
  useEffect(() => {
    if (shouldHideAnalytics || editionsLoading) return;

    setHotColdLoading(true);
    getHotColdSubscribers(editionsToFetch)
      .then(setHotCold)
      .catch(console.error)
      .finally(() => setHotColdLoading(false));
  }, [editionsToFetch, shouldHideAnalytics, editionsLoading]);

  // Redirect if analytics should be hidden
  useEffect(() => {
    if (shouldHideAnalytics) {
      navigate("/dashboard");
    }
  }, [shouldHideAnalytics, navigate]);

  const updateUrlParams = useCallback(
    (
      newStartDate: Date | null,
      newEndDate: Date | null,
      newEditionIds: number[],
      step: StepOption
    ) => {
      const newParams = new URLSearchParams();
      if (newStartDate && newEndDate) {
        newParams.set("start_date", format(newStartDate, "yyyy-MM-dd"));
        newParams.set("end_date", format(newEndDate, "yyyy-MM-dd"));
      }
      if (newEditionIds.length > 0 && newEditionIds.length < editions.length) {
        newParams.set("editions", newEditionIds.join(","));
      }
      if (step !== "daily") {
        newParams.set("step", step);
      }
      navigate(
        { pathname: location.pathname, search: newParams.toString() },
        { replace: true }
      );
    },
    [navigate, location.pathname, editions.length]
  );

  const handleDateChange = useCallback(
    (newDates: [Date | null, Date | null]) => {
      setDateRange(newDates);
      const [newStartDate, newEndDate] = newDates;
      if (newStartDate && newEndDate) {
        updateUrlParams(newStartDate, newEndDate, selectedEditionsIds, selectedStep);
      }
    },
    [updateUrlParams, selectedEditionsIds, selectedStep]
  );

  const handleEditionsChange = useCallback(
    (newEditionIds: number[]) => {
      setSelectedEditionsIds(newEditionIds);
      updateUrlParams(startDate, endDate, newEditionIds, selectedStep);
    },
    [updateUrlParams, startDate, endDate, selectedStep]
  );

  const handleStepChange = useCallback(
    (newStep: StepOption) => {
      setSelectedStep(newStep);
      updateUrlParams(startDate, endDate, selectedEditionsIds, newStep);
    },
    [updateUrlParams, startDate, endDate, selectedEditionsIds]
  );

  const toggleAreaSeries = useCallback((dataKey: string) => {
    setHiddenAreaSeries((prev) => {
      const next = new Set(prev);
      if (next.has(dataKey)) {
        next.delete(dataKey);
      } else {
        next.add(dataKey);
      }
      return next;
    });
  }, []);

  const toggleBarSeries = useCallback((dataKey: string) => {
    setHiddenBarSeries((prev) => {
      const next = new Set(prev);
      if (next.has(dataKey)) {
        next.delete(dataKey);
      } else {
        next.add(dataKey);
      }
      return next;
    });
  }, []);

  const summaryStats = useMemo(() => {
    if (dailyRollup.length === 0) return null;

    const totalDelivered = dailyRollup.reduce((sum, day) => sum + day.delivered, 0);
    const totalOpened = dailyRollup.reduce((sum, day) => sum + day.unique_opened, 0);
    const totalClicked = dailyRollup.reduce((sum, day) => sum + day.unique_clicked, 0);
    const totalBounced = dailyRollup.reduce((sum, day) => sum + day.bounced, 0);
    const totalUnsubscribed = dailyRollup.reduce((sum, day) => sum + day.unsubscribed, 0);

    const openRate = totalDelivered > 0
      ? ((totalOpened / totalDelivered) * 100).toFixed(1)
      : "0.0";
    const clickRate = totalDelivered > 0
      ? ((totalClicked / totalDelivered) * 100).toFixed(1)
      : "0.0";

    return {
      totalDelivered,
      totalOpened,
      totalClicked,
      totalBounced,
      totalUnsubscribed,
      openRate,
      clickRate,
    };
  }, [dailyRollup]);

  const tickFormatter = useCallback(
    (day: string) => formatLabel(day, selectedStep),
    [selectedStep]
  );

  const filteredEditionTotals = useMemo(() => {
    if (!hideEmptyEditions) return editionTotals;
    const visibleKeys = (["delivered", "unique_opened", "unique_clicked"] as const).filter(
      (k) => !hiddenBarSeries.has(k)
    );
    if (visibleKeys.length === 0) return editionTotals;
    return editionTotals.filter((e) => visibleKeys.some((k) => e[k] > 0));
  }, [editionTotals, hideEmptyEditions, hiddenBarSeries]);

  const netGrowth = useMemo(() => {
    if (subscriberGrowth.length === 0) return null;
    const totalNew = subscriberGrowth.reduce((sum, d) => sum + d.new_subscribers, 0);
    const totalLost = subscriberGrowth.reduce((sum, d) => sum + d.lost_subscribers, 0);
    return totalNew - totalLost;
  }, [subscriberGrowth]);

  const pieData = useMemo(() => {
    if (!hotCold) return [];
    return [
      { name: "Hot Subscribers", value: hotCold.hot_count },
      { name: "Cold Subscribers", value: hotCold.cold_count },
    ];
  }, [hotCold]);

  const renderLegend = (
    hiddenSet: Set<string>,
    toggleFn: (key: string) => void
  ) => {
    return (props: any) => {
      const { payload } = props;
      return (
        <div style={{ display: "flex", justifyContent: "center", gap: "16px", flexWrap: "wrap" }}>
          {payload.map((entry: any) => (
            <span
              key={entry.dataKey || entry.value}
              onClick={() => toggleFn(entry.dataKey || entry.value)}
              style={{
                cursor: "pointer",
                opacity: hiddenSet.has(entry.dataKey || entry.value) ? 0.35 : 1,
                textDecoration: hiddenSet.has(entry.dataKey || entry.value) ? "line-through" : "none",
                color: entry.color,
                fontSize: "13px",
                display: "flex",
                alignItems: "center",
                gap: "4px",
              }}
            >
              <span
                style={{
                  display: "inline-block",
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  background: entry.color,
                }}
              />
              {entry.value}
            </span>
          ))}
        </div>
      );
    };
  };

  if (shouldHideAnalytics) {
    return null;
  }

  const cardStyle: React.CSSProperties = {
    background: "#fff",
    borderRadius: "12px",
    border: "1px solid #e2e8f0",
    boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
    padding: "20px",
  };

  const statCardStyle: React.CSSProperties = {
    flex: 1,
    padding: "16px 12px",
    borderRadius: "8px",
    background: "#f7fafc",
    textAlign: "center",
  };

  const statLabelStyle: React.CSSProperties = {
    margin: 0,
    fontSize: "13px",
    color: "#718096",
    fontWeight: 500,
  };

  const statValueStyle: React.CSSProperties = {
    margin: "4px 0 0",
    fontSize: "24px",
    fontWeight: "bold",
  };

  const sectionTitleStyle: React.CSSProperties = {
    fontSize: "16px",
    fontWeight: 600,
    marginBottom: "16px",
    color: "#2d3748",
    margin: "0 0 16px 0",
  };

  return (
    <div>
      {/* Shimmer keyframes */}
      <style>{`
        @keyframes shimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
      `}</style>

      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <h1>Analytics</h1>
        <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
          <div style={{ display: "flex", borderRadius: "6px", overflow: "hidden", border: "1px solid #e2e8f0" }}>
            {STEP_OPTIONS.map((option, idx) => (
              <button
                key={option.value}
                type="button"
                onClick={() => handleStepChange(option.value)}
                style={{
                  padding: "8px 12px",
                  border: "none",
                  borderRight: idx < STEP_OPTIONS.length - 1 ? "1px solid #e2e8f0" : "none",
                  backgroundColor: selectedStep === option.value ? "#5d5ded" : "#fff",
                  color: selectedStep === option.value ? "#fff" : "#4A5568",
                  cursor: "pointer",
                  fontSize: "13px",
                  fontWeight: selectedStep === option.value ? 600 : 400,
                }}
              >
                {option.label}
              </button>
            ))}
          </div>
          <EditionMultiSelect
            options={editions}
            selectedValues={selectedEditionsIds}
            onChange={handleEditionsChange}
            loading={editionsLoading}
          />
          <div style={{ display: "flex", borderRadius: "6px", overflow: "hidden", border: "1px solid #e2e8f0" }}>
            {DATE_PRESETS.map((preset) => {
              const [presetStart, presetEnd] = preset.getRange();
              const isActive =
                startDate &&
                endDate &&
                format(startDate, "yyyy-MM-dd") === format(presetStart, "yyyy-MM-dd") &&
                format(endDate, "yyyy-MM-dd") === format(presetEnd, "yyyy-MM-dd");
              return (
                <button
                  key={preset.label}
                  type="button"
                  onClick={() => handleDateChange([presetStart, presetEnd])}
                  style={{
                    padding: "8px 10px",
                    border: "none",
                    borderRight: "1px solid #e2e8f0",
                    backgroundColor: isActive ? "#5d5ded" : "#fff",
                    color: isActive ? "#fff" : "#4A5568",
                    cursor: "pointer",
                    fontSize: "12px",
                    fontWeight: isActive ? 600 : 400,
                    whiteSpace: "nowrap",
                  }}
                >
                  {preset.label}
                </button>
              );
            })}
          </div>
          <DatePicker
            selectsRange
            startDate={startDate}
            endDate={endDate}
            onChange={handleDateChange}
            minDate={MIN_START_DATE}
            maxDate={TODAY}
            dateFormat="MM/dd/yyyy"
            placeholderText="Select date range"
            popperPlacement="bottom"
          />
        </div>
      </div>

      {/* Summary Stat Cards */}
      <div style={{ ...cardStyle, marginBottom: "24px" }}>
        <div style={{ display: "flex", gap: "12px" }}>
          {rollupLoading ? (
            <>
              <SkeletonStatCard />
              <SkeletonStatCard />
              <SkeletonStatCard />
              <SkeletonStatCard />
              <SkeletonStatCard />
            </>
          ) : summaryStats ? (
            <>
              <div style={statCardStyle}>
                <p style={statLabelStyle}>Sent</p>
                <p style={{ ...statValueStyle, color: COLORS.delivered }}>
                  {summaryStats.totalDelivered.toLocaleString()}
                </p>
              </div>
              <div style={statCardStyle}>
                <p style={statLabelStyle}>Unique Opens</p>
                <p style={{ ...statValueStyle, color: COLORS.unique_opened }}>
                  {summaryStats.totalOpened.toLocaleString()}
                </p>
              </div>
              <div style={statCardStyle}>
                <p style={statLabelStyle}>Open Rate</p>
                <p style={{ ...statValueStyle, color: COLORS.unique_opened }}>
                  {summaryStats.openRate}%
                </p>
              </div>
              <div style={statCardStyle}>
                <p style={statLabelStyle}>Bounces</p>
                <p style={{ ...statValueStyle, color: COLORS.bounced }}>
                  {summaryStats.totalBounced.toLocaleString()}
                </p>
              </div>
              <div style={statCardStyle}>
                <p style={statLabelStyle}>Unsubscribes</p>
                <p style={{ ...statValueStyle, color: COLORS.unsubscribed }}>
                  {summaryStats.totalUnsubscribed.toLocaleString()}
                </p>
              </div>
            </>
          ) : (
            <p style={{ textAlign: "center", width: "100%", color: "#a0aec0" }}>
              No data available for this range.
            </p>
          )}
        </div>
      </div>

      {/* Daily Metrics Area Chart */}
      <div style={{ ...cardStyle, marginBottom: "24px" }}>
        <p style={sectionTitleStyle}>{selectedStep.charAt(0).toUpperCase() + selectedStep.slice(1)} Metrics</p>
        {rollupLoading ? (
          <SkeletonRect height={452} />
        ) : dailyRollup.length === 0 ? (
          <p style={{ textAlign: "center", color: "#a0aec0" }}>
            No data available for this range.
          </p>
        ) : (
          <ResponsiveContainer width="100%" height={452}>
            <AreaChart data={dailyRollup}>
              <defs>
                <linearGradient id="grad_delivered" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={COLORS.delivered} stopOpacity={0.8} />
                  <stop offset="95%" stopColor={COLORS.delivered} stopOpacity={0} />
                </linearGradient>
                <linearGradient id="grad_opened" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={COLORS.opened} stopOpacity={0.8} />
                  <stop offset="95%" stopColor={COLORS.opened} stopOpacity={0} />
                </linearGradient>
                <linearGradient id="grad_unique_opened" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={COLORS.unique_opened} stopOpacity={0.8} />
                  <stop offset="95%" stopColor={COLORS.unique_opened} stopOpacity={0} />
                </linearGradient>
                <linearGradient id="grad_clicked" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={COLORS.clicked} stopOpacity={0.8} />
                  <stop offset="95%" stopColor={COLORS.clicked} stopOpacity={0} />
                </linearGradient>
                <linearGradient id="grad_unique_clicked" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={COLORS.unique_clicked} stopOpacity={0.8} />
                  <stop offset="95%" stopColor={COLORS.unique_clicked} stopOpacity={0} />
                </linearGradient>
              </defs>

              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="day" tickFormatter={tickFormatter} />
              <YAxis />
              <Tooltip labelFormatter={tickFormatter} />
              <Legend content={renderLegend(hiddenAreaSeries, toggleAreaSeries)} />
              <Area
                type="monotone"
                dataKey="delivered"
                stroke={COLORS.delivered}
                fill="url(#grad_delivered)"
                name="Sent"
                hide={hiddenAreaSeries.has("delivered")}
              />
              <Area
                type="monotone"
                dataKey="opened"
                stroke={COLORS.opened}
                fill="url(#grad_opened)"
                name="Total Opens"
                hide={hiddenAreaSeries.has("opened")}
              />
              <Area
                type="monotone"
                dataKey="unique_opened"
                stroke={COLORS.unique_opened}
                fill="url(#grad_unique_opened)"
                name="Unique Opens"
                hide={hiddenAreaSeries.has("unique_opened")}
              />
              <Area
                type="monotone"
                dataKey="clicked"
                stroke={COLORS.clicked}
                fill="url(#grad_clicked)"
                name="Total Clicks"
                hide={hiddenAreaSeries.has("clicked")}
              />
              <Area
                type="monotone"
                dataKey="unique_clicked"
                stroke={COLORS.unique_clicked}
                fill="url(#grad_unique_clicked)"
                name="Unique Clicks"
                hide={hiddenAreaSeries.has("unique_clicked")}
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Subscriber Growth Chart */}
      <div style={{ ...cardStyle, marginBottom: "24px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
          <div>
            <p style={{ ...sectionTitleStyle, marginBottom: netGrowth !== null && growthTab === "daily_change" ? "4px" : "0" }}>Subscriber Growth</p>
            {netGrowth !== null && growthTab === "daily_change" && (
              <p style={{ margin: 0, fontSize: "22px", fontWeight: "bold", color: netGrowth >= 0 ? "#38A169" : "#E53E3E" }}>
                {netGrowth >= 0 ? "+" : ""}{netGrowth.toLocaleString()}
              </p>
            )}
          </div>
          <div style={{ display: "flex", borderRadius: "6px", overflow: "hidden", border: "1px solid #e2e8f0" }}>
            <button
              type="button"
              onClick={() => setGrowthTab("total")}
              style={{
                padding: "6px 14px",
                border: "none",
                borderRight: "1px solid #e2e8f0",
                backgroundColor: growthTab === "total" ? "#5d5ded" : "#fff",
                color: growthTab === "total" ? "#fff" : "#4A5568",
                cursor: "pointer",
                fontSize: "13px",
                fontWeight: growthTab === "total" ? 600 : 400,
              }}
            >
              Total
            </button>
            <button
              type="button"
              onClick={() => setGrowthTab("daily_change")}
              style={{
                padding: "6px 14px",
                border: "none",
                backgroundColor: growthTab === "daily_change" ? "#5d5ded" : "#fff",
                color: growthTab === "daily_change" ? "#fff" : "#4A5568",
                cursor: "pointer",
                fontSize: "13px",
                fontWeight: growthTab === "daily_change" ? 600 : 400,
              }}
            >
              Daily Change
            </button>
          </div>
        </div>
        {growthLoading ? (
          <SkeletonRect height={300} />
        ) : subscriberGrowth.length === 0 ? (
          <p style={{ textAlign: "center", color: "#a0aec0" }}>
            No subscriber data available for this range.
          </p>
        ) : growthTab === "total" ? (
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={subscriberGrowth}>
              <defs>
                <linearGradient id="grad_subscribers" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={COLORS.subscribers} stopOpacity={0.8} />
                  <stop offset="95%" stopColor={COLORS.subscribers} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="day" tickFormatter={tickFormatter} />
              <YAxis />
              <Tooltip
                content={({ active, payload, label }) => {
                  if (!active || !payload?.length) return null;
                  const current = payload[0].value as number;
                  const idx = subscriberGrowth.findIndex((d) => d.day === label);
                  const prev = idx > 0 ? subscriberGrowth[idx - 1].total_subscribers : null;
                  const diff = prev !== null ? current - prev : null;
                  const pct = prev !== null && prev > 0 ? ((diff! / prev) * 100).toFixed(1) : null;
                  return (
                    <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: "8px", padding: "12px", boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
                      <p style={{ margin: "0 0 6px", fontWeight: "bold" }}>{tickFormatter(label)}</p>
                      <p style={{ margin: "2px 0", color: COLORS.subscribers }}>
                        Total: {current.toLocaleString()}
                      </p>
                      {diff !== null && (
                        <p style={{ margin: "2px 0", color: diff >= 0 ? "#38A169" : "#E53E3E" }}>
                          {diff >= 0 ? "+" : ""}{diff.toLocaleString()} ({diff >= 0 ? "+" : ""}{pct}%)
                        </p>
                      )}
                    </div>
                  );
                }}
              />
              <Area
                type="monotone"
                dataKey="total_subscribers"
                stroke={COLORS.subscribers}
                fill="url(#grad_subscribers)"
                name="Total Subscribers"
              />
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={subscriberGrowth}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="day" tickFormatter={tickFormatter} />
              <YAxis />
              <Tooltip
                content={({ active, payload, label }) => {
                  if (!active || !payload?.length) return null;
                  const entry = subscriberGrowth.find((d) => d.day === label);
                  if (!entry) return null;
                  const net = entry.new_subscribers - entry.lost_subscribers;
                  return (
                    <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: "8px", padding: "12px", boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
                      <p style={{ margin: "0 0 6px", fontWeight: "bold" }}>{tickFormatter(label)}</p>
                      <p style={{ margin: "2px 0", color: "#38A169" }}>
                        Subscribed: +{entry.new_subscribers.toLocaleString()}
                      </p>
                      <p style={{ margin: "2px 0", color: "#E53E3E" }}>
                        Unsubscribed: -{entry.lost_subscribers.toLocaleString()}
                      </p>
                      <p style={{ margin: "4px 0 0", fontWeight: 600, color: net >= 0 ? "#38A169" : "#E53E3E" }}>
                        Net: {net >= 0 ? "+" : ""}{net.toLocaleString()}
                      </p>
                    </div>
                  );
                }}
              />
              <Legend />
              <Bar dataKey="new_subscribers" fill="#38A169" name="Subscribed" radius={[4, 4, 0, 0]} />
              <Bar dataKey="lost_subscribers" fill="#E53E3E" name="Unsubscribed" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Two-column row: Pie Chart + Bar Chart */}
      <div style={{ display: "flex", gap: "24px", marginBottom: "24px" }}>
        {/* Hot vs Cold Pie Chart */}
        <div style={{ ...cardStyle, flex: 1, minWidth: 0 }}>
          <p style={sectionTitleStyle}>Hot vs Cold Subscribers</p>
          <p style={{ fontSize: "12px", color: "#a0aec0", marginTop: "-8px", marginBottom: "12px" }}>
            {editionsToFetch.length > 0
              ? `Based on ${editionsToFetch.length} selected edition${editionsToFetch.length > 1 ? "s" : ""}`
              : "Based on your 3 most recent editions"}
          </p>
          {hotColdLoading ? (
            <div style={{ display: "flex", justifyContent: "center", padding: "20px 0" }}>
              <SkeletonCircle size={200} />
            </div>
          ) : hotCold && hotCold.total_active > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={pieData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  label={({ name, value, percent }) =>
                    `${name}: ${value.toLocaleString()} (${(percent * 100).toFixed(1)}%)`
                  }
                >
                  {pieData.map((_, index) => (
                    <Cell key={`cell-${index}`} fill={PIE_COLORS[index]} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value: number, name: string) => [
                    `${value.toLocaleString()} (${hotCold.total_active > 0 ? ((value / hotCold.total_active) * 100).toFixed(1) : 0}%)`,
                    name,
                  ]}
                />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <p style={{ textAlign: "center", color: "#a0aec0" }}>
              No subscriber data available.
            </p>
          )}
        </div>

        {/* Per-Edition Bar Chart */}
        <div style={{ ...cardStyle, flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
            <p style={{ ...sectionTitleStyle, margin: 0 }}>Per Edition Breakdown</p>
            <label style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "12px", color: "#718096", cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={hideEmptyEditions}
                onChange={(e) => setHideEmptyEditions(e.target.checked)}
              />
              Hide empty editions
            </label>
          </div>
          {editionTotalsLoading ? (
            <SkeletonRect height={300} />
          ) : filteredEditionTotals.length === 0 ? (
            <p style={{ textAlign: "center", color: "#a0aec0" }}>
              No edition data available for this range.
            </p>
          ) : (
            <>
              <div style={{ marginBottom: "12px" }}>
                {renderLegend(hiddenBarSeries, toggleBarSeries)({
                  payload: [
                    { dataKey: "delivered", value: "Delivered", color: COLORS.delivered },
                    { dataKey: "unique_opened", value: "Unique Opens", color: COLORS.unique_opened },
                    { dataKey: "unique_clicked", value: "Unique Clicks", color: COLORS.unique_clicked },
                  ],
                })}
              </div>
              <div style={{ maxHeight: "350px", overflowY: "auto", flex: 1 }}>
                <ResponsiveContainer width="100%" height={Math.max(300, filteredEditionTotals.length * 60)}>
                  <BarChart
                    data={filteredEditionTotals}
                    layout="vertical"
                    margin={{ left: 20, right: 20 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis
                      dataKey="title"
                      type="category"
                      width={140}
                      tick={{ fontSize: 12 }}
                      tickFormatter={(value: string) =>
                        value.length > MAX_EDITION_LABEL_LENGTH
                          ? value.slice(0, MAX_EDITION_LABEL_LENGTH) + "…"
                          : value
                      }
                    />
                    <Tooltip content={<EditionBarTooltip />} />
                    <Bar
                      dataKey="delivered"
                      fill={COLORS.delivered}
                      name="Delivered"
                      hide={hiddenBarSeries.has("delivered")}
                    />
                    <Bar
                      dataKey="unique_opened"
                      fill={COLORS.unique_opened}
                      name="Unique Opens"
                      hide={hiddenBarSeries.has("unique_opened")}
                    />
                    <Bar
                      dataKey="unique_clicked"
                      fill={COLORS.unique_clicked}
                      name="Unique Clicks"
                      hide={hiddenBarSeries.has("unique_clicked")}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Analytics;
